/**
 * The onboarding questions, as data.
 *
 * Aligned to the product's own onboarding (`app/src/data/onboardingFlow.ts` and
 * `docs/product/onboarding-flow.md`) rather than invented for the funnel: the
 * moods, viewing pace, faith opt-in and developmental goals all exist there and
 * map onto the 14 canonical content worlds. A parent who answers here should not
 * be asked the same thing again inside the app.
 *
 * The prototype's four growth questions (`skill`, `challenge`, `learn`, `wish`)
 * are gone. They mapped to nothing — no content world, no recommendation — and
 * overlapped what `child-goals` and `story-moods` already ask properly.
 *
 * Copy is US English, because the market is the US. That means `Mom` and `Colors`.
 */

/** A content world from `docs/product/content-worlds.md`. Every topic maps to
 *  one, so an answer is routable rather than decorative. */
export type World =
  | 'Everyday Wins' | 'Feelings & Friends' | 'Calm & Cozy' | 'Big Adventures'
  | 'Animals & Nature' | 'Wonder Lab' | 'Ready for School' | 'Music & Movement'
  | 'Family Stories' | 'Community Heroes' | 'Safe & Strong' | 'Faith & Values'
  | 'Special Days' | 'Silly Shorts';

export interface Option {
  v: string;
  label: string;
  /** Second line, for options that need explaining (viewing pace). */
  copy?: string;
  emo?: string;
  world?: World;
}

// ── Age ───────────────────────────────────────────────────────────────────────

/**
 * Five developmental bands covering 12 months to 9 years with no gaps.
 *
 * Bands rather than an exact birth date: a date picker is the heaviest input in
 * the flow, and a child's date of birth is personal data being collected before
 * the parent has bought anything. The app still takes the exact date after
 * sign-in, which is what birthday stories need.
 */
export const AGE_BANDS: Option[] = [
  { v: 'age-1', label: '1 year' },
  { v: 'age-2', label: '2 years' },
  { v: 'age-3-4', label: '3–4 years' },
  { v: 'age-5-6', label: '5–6 years' },
  { v: 'age-7-9', label: '7–9 years' },
];

/**
 * What is going on for a child of this age, keyed by band.
 *
 * The same subject changes meaning with age, so it is worded per band rather
 * than shared: brushing teeth is the parent's job at one, a battle at two, and
 * something done alone at five.
 */
export const TOPICS_BY_AGE: Record<string, Option[]> = {
  'age-1': [
    { v: 'sleep-night', label: 'Sleeping through the night', world: 'Everyday Wins' },
    { v: 'weaning', label: 'Weaning from breast or bottle', world: 'Everyday Wins' },
    { v: 'teeth', label: 'Brushing teeth', world: 'Everyday Wins' },
    { v: 'bath', label: 'Bath time without tears', world: 'Everyday Wins' },
    { v: 'spoon', label: 'Eating with a spoon', world: 'Everyday Wins' },
    { v: 'dressing', label: 'Getting dressed', world: 'Everyday Wins' },
    { v: 'first-words', label: 'First words', world: 'Ready for School' },
    { v: 'goodbye', label: 'Saying goodbye without tears', world: 'Feelings & Friends' },
    { v: 'winding-down', label: 'Winding down for bed', world: 'Calm & Cozy' },
    { v: 'songs', label: 'Songs and moving about', world: 'Music & Movement' },
    { v: 'animals', label: 'Naming the animals', world: 'Animals & Nature' },
    { v: 'grandparents', label: 'Grandma and Grandpa', world: 'Family Stories' },
  ],
  'age-2': [
    { v: 'potty', label: 'Potty training', world: 'Everyday Wins' },
    { v: 'teeth', label: 'Brushing teeth without a fight', world: 'Everyday Wins' },
    { v: 'own-bed', label: 'Sleeping in their own bed', world: 'Everyday Wins' },
    { v: 'dressing', label: 'Getting dressed alone', world: 'Everyday Wins' },
    { v: 'tidying', label: 'Tidying up the toys', world: 'Everyday Wins' },
    { v: 'dentist', label: 'First trip to the dentist', world: 'Everyday Wins' },
    { v: 'tantrums', label: 'Big feelings and tantrums', world: 'Feelings & Friends' },
    { v: 'sharing', label: 'Sharing toys', world: 'Feelings & Friends' },
    { v: 'nursery', label: 'Starting nursery', world: 'Special Days' },
    { v: 'new-baby', label: 'A new baby in the family', world: 'Family Stories' },
    { v: 'colors', label: 'Colors and shapes', world: 'Ready for School' },
    { v: 'calming', label: 'Calming down and breathing', world: 'Calm & Cozy' },
  ],
  'age-3-4': [
    { v: 'friends', label: 'Making friends', world: 'Feelings & Friends' },
    { v: 'fears', label: 'Fears and the dark', world: 'Feelings & Friends' },
    { v: 'waiting', label: 'Waiting your turn', world: 'Feelings & Friends' },
    { v: 'sorry', label: 'Saying sorry', world: 'Feelings & Friends' },
    { v: 'jealous', label: 'Feeling jealous', world: 'Feelings & Friends' },
    { v: 'doctor', label: 'Doctor visits', world: 'Everyday Wins' },
    { v: 'dentist', label: 'Dentist visits', world: 'Everyday Wins' },
    { v: 'haircut', label: 'Haircuts', world: 'Everyday Wins' },
    { v: 'dressing', label: 'Dressing without help', world: 'Everyday Wins' },
    { v: 'road', label: 'Road safety', world: 'Safe & Strong' },
    { v: 'letters', label: 'Letters and numbers', world: 'Ready for School' },
    { v: 'heroes', label: 'Firefighters, vets and doctors', world: 'Community Heroes' },
  ],
  'age-5-6': [
    { v: 'school-ready', label: 'Getting school-ready', world: 'Ready for School' },
    { v: 'first-day', label: 'First day of school', world: 'Special Days' },
    { v: 'problems', label: 'Solving problems', world: 'Ready for School' },
    { v: 'friend-trouble', label: 'Friendship troubles', world: 'Feelings & Friends' },
    { v: 'helping', label: 'Helping at home', world: 'Everyday Wins' },
    { v: 'teeth', label: 'Brushing teeth on their own', world: 'Everyday Wins' },
    { v: 'safety', label: 'Road and water safety', world: 'Safe & Strong' },
    { v: 'lost', label: 'What to do if I get lost', world: 'Safe & Strong' },
    { v: 'boundaries', label: 'Body boundaries', world: 'Safe & Strong' },
    { v: 'how-things-work', label: 'How things work', world: 'Wonder Lab' },
    { v: 'pet', label: 'Looking after a pet', world: 'Animals & Nature' },
    { v: 'birthdays', label: 'Birthdays', world: 'Special Days' },
  ],
  'age-7-9': [
    { v: 'big-feelings', label: 'Managing big feelings', world: 'Feelings & Friends' },
    { v: 'left-out', label: 'Friendship troubles and being left out', world: 'Feelings & Friends' },
    { v: 'chores', label: 'Responsibility and chores', world: 'Everyday Wins' },
    { v: 'habits', label: 'Healthy habits', world: 'Everyday Wins' },
    { v: 'how-things-work', label: 'How things work', world: 'Wonder Lab' },
    { v: 'body', label: 'The body and how it works', world: 'Wonder Lab' },
    { v: 'calming', label: 'Calming down and breathing', world: 'Calm & Cozy' },
    { v: 'nature', label: 'Looking after nature', world: 'Animals & Nature' },
    { v: 'jobs', label: 'Jobs and what I want to be', world: 'Community Heroes' },
    { v: 'help', label: 'Calling for help, fire safety', world: 'Safe & Strong' },
    { v: 'family-changes', label: 'Family changes', world: 'Family Stories' },
    { v: 'travel', label: 'Travel and holidays', world: 'Special Days' },
  ],
};

// ── The rest of the question set ──────────────────────────────────────────────

/**
 * Narration form.
 *
 * Two options by product decision. The app's `childPronouns` also offers
 * "They / them" and "Just use their name"; if those come back, they belong here
 * and in the character look, which currently only knows `isGirl`.
 */
export const PRONOUNS: Option[] = [
  { v: 'she', label: 'Girl', emo: '👧' },
  { v: 'he', label: 'Boy', emo: '👦' },
];

/** Mirrors the app's `storyMoodOptions`, world mapping included. */
export const MOODS: Option[] = [
  { v: 'adventures', label: 'Big adventures', emo: '🚀', world: 'Big Adventures' },
  { v: 'animals', label: 'Animals & nature', emo: '🦊', world: 'Animals & Nature' },
  { v: 'calm', label: 'Calm & cozy', emo: '🌙', world: 'Calm & Cozy' },
  { v: 'music', label: 'Music & movement', emo: '🎵', world: 'Music & Movement' },
  { v: 'family', label: 'Family stories', emo: '🏡', world: 'Family Stories' },
  { v: 'silly', label: 'Silly shorts', emo: '😄', world: 'Silly Shorts' },
];

/** Mirrors the app's `viewingPaceOptions`, copy verbatim. */
export const PACE: Option[] = [
  { v: 'calm', label: 'Calm & low stimulation', copy: 'Longer shots, gentle movement, soft sound, and extra breathing room.' },
  { v: 'balanced', label: 'A little of everything', copy: 'A balanced mix of cozy stories, learning, songs, and adventure.' },
  { v: 'lively', label: 'Bright & playful', copy: 'More movement, music, comedy, and energetic adventures.' },
];

/**
 * Faith opt-in. Explicit, never inferred — the product doc is unambiguous:
 * "Lenny never infers religion".
 *
 * NOTE: even this yes/no reveals religious belief, which GDPR Article 9 treats
 * as a special category. It is safe while the funnel keeps everything in memory;
 * persisting it needs an explicit consent basis of its own.
 */
export const FAITH_CHOICE: Option[] = [
  { v: 'yes', label: 'Yes, please' },
  { v: 'no', label: 'No thanks' },
  { v: 'later', label: 'Maybe later' },
];

/** First step of the tradition picker — the broad family. */
export const FAITH_GROUPS: Option[] = [
  { v: 'christian', label: 'Christian' },
  { v: 'jewish', label: 'Jewish' },
  { v: 'muslim', label: 'Muslim' },
  { v: 'dharmic', label: 'Hindu, Buddhist, Sikh or Jain' },
  { v: 'other', label: 'Another tradition' },
  { v: 'values', label: 'Values and kindness, without religion' },
];

/**
 * Second step, per group. Each branchable group carries a "no branch" escape:
 * asking which branch and then serving the same story either way is worse than
 * not asking, because the parent has told you something you ignored.
 *
 * `values` has no second step.
 */
export const FAITH_BRANCHES: Record<string, Option[]> = {
  christian: [
    { v: 'evangelical', label: 'Evangelical' },
    { v: 'mainline', label: 'Mainline Protestant' },
    { v: 'catholic', label: 'Catholic' },
    { v: 'orthodox', label: 'Orthodox' },
    { v: 'nondenom', label: 'Non-denominational' },
    { v: 'lds', label: 'Latter-day Saint' },
    { v: 'adventist', label: 'Seventh-day Adventist' },
    { v: 'jw', label: "Jehovah's Witness" },
    { v: 'christian', label: 'Just Christian' },
  ],
  jewish: [
    { v: 'reform', label: 'Reform' },
    { v: 'conservative', label: 'Conservative' },
    { v: 'orthodox', label: 'Orthodox' },
    { v: 'jewish', label: 'Just Jewish' },
  ],
  muslim: [
    { v: 'sunni', label: 'Sunni' },
    { v: 'shia', label: 'Shia' },
    { v: 'muslim', label: 'Just Muslim' },
  ],
  dharmic: [
    { v: 'hindu', label: 'Hindu' },
    { v: 'buddhist', label: 'Buddhist' },
    { v: 'sikh', label: 'Sikh' },
    { v: 'jain', label: 'Jain' },
  ],
  other: [
    { v: 'bahai', label: 'Bahá’í' },
    { v: 'uu', label: 'Unitarian Universalist' },
    { v: 'indigenous', label: 'Indigenous / Native American' },
    { v: 'pagan', label: 'Pagan / Earth-based' },
    { v: 'another', label: 'Another tradition' },
  ],
};

/**
 * Who else is in the family.
 *
 * The rig can only render the child (Child, Dad and Mom exist; siblings and
 * grandparents do not), so this drives STORIES about those people — content
 * world 09 Family Stories — not their likenesses. The copy promises exactly
 * that and no more.
 */
export const FAMILY: Option[] = [
  { v: 'brother', label: 'Brother', emo: '🧒' },
  { v: 'sister', label: 'Sister', emo: '👧' },
  { v: 'mom', label: 'Mom', emo: '👩' },
  { v: 'dad', label: 'Dad', emo: '👨' },
  { v: 'grandma', label: 'Grandma', emo: '👵' },
  { v: 'grandpa', label: 'Grandpa', emo: '👴' },
  { v: 'auntuncle', label: 'Aunt or uncle', emo: '🧑' },
];

/**
 * The interstitials, one every three questions.
 *
 * Deliberately claim-free of numbers. These rest on well-established findings —
 * the self-reference effect, observational learning — worded so that none of
 * them needs a citation to defend. No statistic here was invented, because a
 * fabricated number shown to a parent about their own child is the worst kind
 * of copy to get wrong.
 */
export interface Fact {
  /** May contain `{name}` — filled with the child's name at render. */
  title: string;
  body: string;
  emo: string;
  /** Silent 5s clip in `assets/clips/`, generated by scripts/generate-fact-clips.mjs. */
  clip: string;
  /** Phone-shaped 9:16 clip, shown narrower and taller than the landscape ones. */
  portrait?: boolean;
  /** Small line under the body, e.g. who makes this. */
  note?: string;
}

/**
 * Topics that have their own clip for the "children try what they have
 * watched" fact screen: a real child of that age watching the cartoon of
 * themselves doing exactly that thing. When the parent picked one of these,
 * the screen shows it instead of the generic tooth-brushing clip. Generated
 * by scripts/generate-topic-clips.mjs.
 */
export const TOPIC_CLIPS: Record<string, string> = {
  spoon: '/clips/topics/spoon.mp4',
  animals: '/clips/topics/animals.mp4',
  potty: '/clips/topics/potty.mp4',
  sharing: '/clips/topics/sharing.mp4',
  fears: '/clips/topics/fears.mp4',
  haircut: '/clips/topics/haircut.mp4',
  helping: '/clips/topics/helping.mp4',
  'first-day': '/clips/topics/first-day.mp4',
  chores: '/clips/topics/chores.mp4',
  'big-feelings': '/clips/topics/big-feelings.mp4',
  jobs: '/clips/topics/jobs.mp4',
};

/**
 * The playlist for the "children copy what they watch" screen: the clips of
 * the picked topics that have one, in pick order, then the other clips of the
 * child's age band (Jernej, 2026-09-08: the age clips play there too), so a
 * parent who skipped the question still sees a child of the right age.
 * Null when nothing matches, and the generic clip plays.
 */
export const topicClipsFor = (topics: string[], age?: string): string[] | null => {
  const picked = topics.map((t) => TOPIC_CLIPS[t]).filter((c): c is string => !!c);
  const band = (age && TOPICS_BY_AGE[age]) ?? [];
  const forAge = band.map((o) => TOPIC_CLIPS[o.v]).filter((c): c is string => !!c && !picked.includes(c));
  const clips = [...picked, ...forAge];
  return clips.length ? clips : null;
};

export const FACTS: Fact[] = [
  {
    emo: '🧠',
    clip: '/clips/fact-1-name.mp4',
    title: '{name} will remember the story better for being in it.',
    body: 'Hearing their own name pulls a child’s attention back to the screen, and to what the story is teaching.',
  },
  {
    emo: '👣',
    clip: '/clips/fact-2-copy.mp4',
    title: 'Children try what they have watched someone else do.',
    body: 'When that someone looks like {name}, the leap from screen to real life gets shorter.',
  },
  {
    emo: '💛',
    clip: '/clips/fact-3-family.mp4',
    title: 'Familiar faces make a lesson feel like it belongs to real life.',
    body: 'A story with {name}’s own family in it lands differently than one about strangers on television.',
  },
  {
    // Shown right after the topics question: four home clips from the
    // homepage reel, joined into one (scripts/generate-home-clips.mjs).
    emo: '📺',
    clip: '/clips/moment.mp4',
    portrait: true,
    title: 'Made for moments like this.',
    body: 'Children light up when the hero on the screen is them. Every topic you just picked becomes one of these moments for {name}.',
  },
];
